<template>
  <div class="contrato-page min-h-screen relative">
    <FondoDiagonal />

    <!-- Main Content -->
    <div class="relative z-10">
      <!-- Welcome Section -->
      <div class="container mx-auto px-4 lg:px-8 py-8">
        <div class="max-w-7xl mx-auto">
          <!-- Header -->
          <div class="mb-12 animate-fade-in">
            <!-- Greeting Card -->
            <div class="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-8 mb-8 border-l-8 transform hover:scale-[1.02] transition-all duration-300">
              <div class="flex items-center justify-between flex-wrap gap-6">
                <!-- Left: Greeting -->
                <div class="flex-1 min-w-[300px]">
                  <div class="text-5xl md:text-6xl lg:text-7xl font-black text-white">
                    <span class="block">Hola </span>
                    <span class="inline-block bg-gradient-to-r px-6 py-2 rounded-2xl shadow-inner">
                      {{ primerNombre }} 😊
                    </span>
                  </div>
                  <p class="text-2xl md:text-3xl text-white font-semibold leading-relaxed">
                    Aquí comienza tu proceso de matrícula
                  </p>
                </div>

                <!-- Right: User Badge -->
                <div class="flex-shrink-0">
                  <div class="inline-flex items-center gap-4 px-8 py-6 rounded-2xl shadow-xl transform hover:scale-105 transition-all duration-300 bg-verde-institucional">
                    <i class="fas fa-user-graduate text-4xl text-white"></i>
                    <div class="text-white">
                      <div class="text-sm font-medium opacity-90">Postulante</div>
                      <div class="text-xl font-bold">Nuevo</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Career Card -->
            <div class="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-8 relative overflow-hidden animate-slide-in-up">
              <!-- Decorative waves -->
              <img
                class="absolute -left-16 top-1/2 -translate-y-1/2 w-32 opacity-40"
                src="@/assets/img/olas_verdes.png"
                alt="Decoración"
              >

              <div class="relative z-10">
                <div class="text-sm font-semibold text-white uppercase tracking-wide mb-2">
                  Tu Carrera
                </div>
                <h2 class="text-4xl md:text-5xl text-white font-bold mb-4 ">
                  {{ $store.getters.carrera }}
                </h2>
                <div class="flex items-center gap-3 text-2xl md:text-3xl font-bold text-white">
                  <span>UAysén</span>
                  <span class="text-gray-400">•</span>
                  <span class="text-xl font-medium">Departamento de {{ $store.getters.depto }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Video Section -->
          <div class="mb-12 animate-slide-in-up" style="animation-delay: 0.2s">
            <div class="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-6 lg:p-8">
              <div class="flex items-center gap-3 mb-6">
                <div class="w-12 h-12 bg-gradient-to-br from-celeste-institucional to-azul-institucional rounded-xl flex items-center justify-center">
                  <i class="fas fa-play-circle text-white text-2xl"></i>
                </div>
                <h3 class="text-2xl font-bold text-white">
                  Video Informativo
                </h3>
              </div>
              <VideoYoutube url="https://www.youtube-nocookie.com/embed/_frLxCIeo8k" />
            </div>
          </div>

          <!-- Contract Form -->
          <form @submit.prevent="siguiente" class="space-y-8 animate-slide-in-up" style="animation-delay: 0.4s">
            <!-- Important Alert -->
            <div class="bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-naranjo rounded-xl p-6 shadow-lg">
              <div class="flex items-start gap-4">
                <div class="flex-shrink-0 w-12 h-12 bg-naranjo rounded-full flex items-center justify-center">
                  <i class="fas fa-exclamation-triangle text-white text-xl"></i>
                </div>
                <div class="flex-1">
                  <h4 class="text-2xl font-black text-naranjo mb-4 uppercase">
                    ¡Importante!
                  </h4>
                  <ul class="space-y-3 text-gray-700 leading-relaxed">
                    <li class="flex items-start gap-3">
                      <i class="fas fa-chevron-right text-naranjo mt-1 flex-shrink-0"></i>
                      <span>
                        La no concreción del proceso de matrícula de un seleccionado(a) tanto en la primera
                        <strong class="text-naranjo">(20 al 22 de enero)</strong> como en la segunda instancia
                        <strong class="text-naranjo">(23 de enero al 29 de enero)</strong>,
                        implica que renuncia a su vacante, dejando liberado su cupo, sin permitírsele un reclamo posterior.
                      </span>
                    </li>
                    <li class="flex items-start gap-3">
                      <i class="fas fa-chevron-right text-naranjo mt-1 flex-shrink-0"></i>
                      <span>
                        Debes leer y aceptar los términos y condiciones de prestación de servicios educacionales
                        para continuar con tu proceso de matrícula (en caso de no visualizar el documento, por favor recarga la página).
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Terms and Conditions -->
            <div class="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl overflow-hidden">
              <!-- Header -->
              <div class="bg-gradient-to-r from-azul-institucional to-celeste-institucional p-6">
                <div class="flex items-center gap-3">
                  <i class="fas fa-file-contract text-white text-3xl"></i>
                  <h3 class="text-2xl font-bold text-white uppercase">
                    Términos y Condiciones
                  </h3>
                </div>
              </div>

              <!-- PDF Container -->
              <div class="p-6 lg:p-8 bg-gray-50">
                <div class="pdf-container max-h-[600px] overflow-y-auto custom-scrollbar">
                  <pdf
                    v-for="i in numPages"
                    :key="i"
                    :src="src"
                    :page="i"
                    class="pdf-page mb-4 border-4 border-verde-institucional rounded-xl shadow-lg"
                  />
                </div>
              </div>

              <!-- Checkboxes -->
              <div class="p-6 lg:p-8 space-y-6 border-t-2 border-gray-200">
                <!-- Checkbox 1: Accept Vacancy -->
                <label class="checkbox-container group cursor-pointer">
                  <div class="flex items-start gap-4">
                    <div class="relative flex-shrink-0 mt-1">
                      <input
                        type="checkbox"
                        id="declaracion"
                        v-model="aceptoVacante"
                        class="sr-only"
                      >
                      <div class="checkbox-custom w-7 h-7 border-2 rounded-lg transition-all duration-200"
                           :class="aceptoVacante
                             ? 'bg-verde-institucional border-verde-institucional'
                             : 'border-gray-400 group-hover:border-verde-institucional'">
                        <svg v-if="aceptoVacante" class="w-5 h-5 text-white m-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                        </svg>
                      </div>
                    </div>
                    <div class="flex-1">
                      <span class="text-lg text-gray-700 leading-relaxed">
                        Acepto la vacante disponible para estudiar en la carrera de
                        <strong :style="{ color: planTextColor }">{{ $store.getters.carrera }}</strong>
                        de la Universidad de Aysén.
                      </span>
                    </div>
                  </div>
                </label>

                <!-- Checkbox 2: Accept Terms -->
                <label class="checkbox-container group cursor-pointer">
                  <div class="flex items-start gap-4">
                    <div class="relative flex-shrink-0 mt-1">
                      <input
                        type="checkbox"
                        id="contrato"
                        v-model="aceptoContrato"
                        class="sr-only"
                      >
                      <div class="checkbox-custom w-7 h-7 border-2 rounded-lg transition-all duration-200"
                           :class="aceptoContrato
                             ? 'bg-verde-institucional border-verde-institucional'
                             : 'border-gray-400 group-hover:border-verde-institucional'">
                        <svg v-if="aceptoContrato" class="w-5 h-5 text-white m-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                        </svg>
                      </div>
                    </div>
                    <div class="flex-1">
                      <span class="text-lg text-gray-700 leading-relaxed">
                        Acepto los términos y condiciones de prestación de servicios educacionales.
                      </span>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <!-- Submit Button -->
            <div class="flex flex-col items-end gap-4">
              <button
                type="submit"
                :disabled="!canSubmit"
                class="group px-10 py-5 bg-gradient-to-r from-celeste-institucional to-verde-institucional hover:from-verde-institucional hover:to-azul-institucional disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-bold text-xl rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 min-w-64"
              >
                <span v-if="!cargando" class="flex items-center justify-center">
                  <span class="mr-3">Siguiente</span>
                  <i class="fas fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
                </span>
                <span v-else class="flex items-center justify-center">
                  <svg class="animate-spin -ml-1 mr-3 h-6 w-6 text-white" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Procesando...
                </span>
              </button>

              <!-- Error Message -->
              <transition name="shake">
                <div
                  v-if="error"
                  class="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg max-w-2xl animate-shake"
                >
                  <div class="flex items-center">
                    <svg class="w-5 h-5 text-red-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                    </svg>
                    <span class="text-red-700 font-medium text-sm">{{ error }}</span>
                  </div>
                </div>
              </transition>
            </div>
          </form>
        </div>
      </div>

      <!-- Progress Footer -->
      <div class="relative z-10 mt-16 pb-8">
        <ProgresoPasos :paso="1" />
      </div>
    </div>
  </div>
</template>

<script>
import store from '@/store';
import FondoDiagonal from '@/componentes/UI/FondoDiagonal.vue';
import VideoYoutube from '@/componentes/UI/VideoYoutube.vue';
import ProgresoPasos from '@/componentes/ProgresoPasos.vue';
import { aceptarVacante, aceptarContrato } from '@/assets/api/matricula';
import pdf from 'vue-pdf';

const loadingTask = pdf.createLoadingTask('/documentos/TERMINOSYCONDICIONES2026.pdf');

export default {
  name: 'Contrato',
  components: {
    pdf,
    FondoDiagonal,
    ProgresoPasos,
    VideoYoutube,
  },
  data() {
    return {
      src: loadingTask,
      numPages: undefined,
      aceptoVacante: false,
      aceptoContrato: false,
      cargando: false,
      error: '',
    };
  },
  computed: {
    primerNombre() {
      return this.$store.getters.nombre.split(' ')[0];
    },
    canSubmit() {
      return this.aceptoVacante && this.aceptoContrato && !this.cargando;
    },
    planColor() {
      const colorVar = this.$store.getters.colorPlan;
      if (colorVar.includes('var(')) {
        const colorMap = {
          'var(--azul-institucional)': '#375282',
          'var(--verde-institucional)': '#40b99d',
          'var(--celeste-institucional)': '#56c0e8',
          'var(--azul-enfermeria)': '#273470',
          'var(--rojo-obstetricia)': '#AB2326',
          'var(--verde-agronomia)': '#639244',
          'var(--verde-forestal)': '#136E49',
          'var(--naranjo-industrial)': '#F06A26',
          'var(--amarillo-social)': '#F9b81C',
          'var(--celeste-psicologia)': '#00ACB4',
          'var(--rosado-informatica)': '#D60B51',
          'var(--verde-terapia-ocupacional)': '#336666',
          'var(--azul-ing-comercial)': '#006699',
          'var(--naranjo)': '#f27c08',
          'var(--morado-adm-publica)': '#333366',
          'var(--gris-derecho)': '#666666',
          'var(--amarillo-ing-renovables)': '#8c9c27'
        };
        return colorMap[colorVar] || '#375282';
      }
      return colorVar;
    },
    planTextColor() {
      const colorVar = this.$store.getters.colorTextoPlan;
      if (colorVar.includes('var(')) {
        const colorMap = {
          'var(--azul-institucional)': '#375282',
          'var(--verde-institucional)': '#40b99d',
          'var(--celeste-institucional)': '#56c0e8',
          'var(--azul-enfermeria)': '#273470',
          'var(--rojo-obstetricia)': '#AB2326',
          'var(--verde-agronomia)': '#639244',
          'var(--verde-forestal)': '#136E49',
          'var(--naranjo-industrial)': '#F06A26',
          'var(--amarillo-social)': '#F9b81C',
          'var(--celeste-psicologia)': '#00ACB4',
          'var(--rosado-informatica)': '#D60B51',
          'var(--verde-terapia-ocupacional)': '#336666',
          'var(--azul-ing-comercial)': '#006699',
          'var(--naranjo)': '#f27c08',
          'var(--morado-adm-publica)': '#333366',
          'var(--gris-derecho)': '#666666',
          'var(--amarillo-ing-renovables)': '#8c9c27'
        };
        return colorMap[colorVar] || '#375282';
      }
      return colorVar;
    },
  },
  async created() {
    const doc = await loadingTask.promise;
    this.numPages = doc.numPages;
  },
  beforeRouteEnter(to, from, next) {
    // evitar acceso no autenticado
    if (!store.state.sesion) {
      next({ name: store.getters.linkInicio });
      return;
    }
    // evitar que alguien vuelva a entrar en esta vista
    if (store.getters.idEstudiante) {
      next({ name: 'perfil estudiante' });
    } else {
      next();
    }
  },
  methods: {
    siguiente() {
      if (this.aceptoVacante && this.aceptoContrato) {
        this.cargando = true;
        this.error = '';

        aceptarContrato().then(() => {
          aceptarVacante(this.$store.state.idPostulante).then((respuesta) => {
            this.$store.commit('actualizarEstudiante', respuesta.estudiante);
            this.$store.commit('actualizarEtapa', 1);
            this.$router.push({ name: 'perfil estudiante' });
          }).catch(() => {
            this.cargando = false;
            this.error = 'Lo sentimos, ha ocurrido un error en el sistema. Por favor intenta nuevamente y si el problema persiste ponte en contacto con el encargado de matrícula';
          });
        }).catch(() => {
          this.cargando = false;
          this.error = 'Lo sentimos, ha ocurrido un error en el sistema. Por favor intenta nuevamente y si el problema persiste ponte en contacto con el encargado de matrícula';
        });
      }
    },
  },
};
</script>

<style scoped>
/* Page wrapper */
.contrato-page {
  position: relative;
  isolation: isolate;
}

/* Custom colors */
.text-azul-institucional { color: #375282; }
.text-celeste-institucional { color: #56c0e8; }
.text-verde-institucional { color: #40b99d; }
.text-naranjo { color: #f27c08; }

.bg-azul-institucional { background-color: #375282; }
.bg-celeste-institucional { background-color: #56c0e8; }
.bg-verde-institucional { background-color: #40b99d; }
.bg-naranjo { background-color: #f27c08; }

.border-verde-institucional { border-color: #40b99d; }
.border-naranjo { border-color: #f27c08; }

.from-celeste-institucional { --tw-gradient-from: #56c0e8; }
.to-verde-institucional { --tw-gradient-to: #40b99d; }
.from-verde-institucional { --tw-gradient-from: #40b99d; }
.to-azul-institucional { --tw-gradient-to: #375282; }
.from-gray-400 { --tw-gradient-from: #9ca3af; }
.to-gray-500 { --tw-gradient-to: #6b7280; }

.hover\:from-verde-institucional:hover { --tw-gradient-from: #40b99d; }
.hover\:to-azul-institucional:hover { --tw-gradient-to: #375282; }

/* PDF Container */
.pdf-container {
  background: white;
  border-radius: 0.75rem;
  padding: 1rem;
}

.pdf-page {
  max-width: 100%;
  display: block;
  margin: 0 auto;
}

/* Custom Scrollbar */
.custom-scrollbar::-webkit-scrollbar {
  width: 12px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #40b99d;
  border-radius: 10px;
  border: 2px solid #f1f1f1;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #375282;
}

/* Checkbox */
.checkbox-container {
  display: block;
  padding: 1rem;
  background: linear-gradient(to bottom right, #f9fafb, white);
  border-radius: 0.75rem;
  border: 2px solid #e5e7eb;
  transition: all 0.3s ease;
}

.checkbox-container:hover {
  border-color: #40b99d;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.checkbox-custom {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Animations */
@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slide-in-up {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-10px); }
  75% { transform: translateX(10px); }
}

.animate-fade-in {
  animation: fade-in 0.6s ease-out;
}

.animate-slide-in-up {
  animation: slide-in-up 0.6s ease-out;
}

.animate-shake {
  animation: shake 0.5s ease-in-out;
}

/* Shake transition */
.shake-enter-active {
  animation: shake 0.5s ease-in-out;
}

/* Responsive */
@media (max-width: 768px) {
  .pdf-container {
    max-height: 400px;
    padding: 0.75rem;
  }

  .checkbox-container {
    padding: 0.75rem;
  }
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  * {
    animation: none !important;
    transition: none !important;
  }
}

/* Print styles */
@media print {
  .contrato-page {
    background: white !important;
  }

  .bg-gradient-to-r {
    background: white !important;
  }

  .shadow-2xl,
  .shadow-xl,
  .shadow-lg {
    box-shadow: none !important;
  }
}
</style>
